import type { CardDetail } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  archiveCard,
  editCard,
  loadCard,
  loadPriceHistory,
  repriceCard,
  setCardFloor,
  setCardPrice,
  setCardStock,
} from "../../core/ops/cards-ops";
import { formatCents } from "../../core/primitives/card-display";
import {
  ARCHIVE_FAILED_ON_THE_WAY,
  CARD_CHANGED_BEFORE_SAVE,
  CARD_SAVE_FAILED_ON_THE_WAY,
  CARD_SAVE_FAILED_UNREAD,
  CARD_SAVED_UNREAD,
  type CardEditing,
  type CardFields,
  type CardSection,
  cardBase,
  cardIdentityEdit,
  changesIdentity,
  floorCentsOf,
  isDirty,
  isEmptyEdit,
  isSaveable,
  pricesChanged,
  REPRICE_FAILED_ON_THE_WAY,
  rebasedFields,
  type StockLossKind,
  stockUpdate,
} from "../../core/primitives/card-edit";
import { errorText } from "../../core/primitives/errors";
import { loaded } from "../../core/primitives/load-status";
import { Loader } from "../../core/primitives/loader";
import { inputToCents } from "../../core/primitives/scan-edit";
import { can } from "../../core/primitives/store-role";
import { CardDetailActions, CardDetailState } from "./card-detail-model";

export type CardDetailDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "stockListRepository">;

export interface CardDetailRouting {
  back(): void;
  toCards(): void;
}

/**
 * One card the shop sells, on one page, edited in place: the price, floor and
 * count, what the card is, its eBay comps, where its copies are, what it has
 * asked before, where it is listed, and archiving. Everything typed waits for
 * Save. A port of the iOS `AdminCardScreen`, reached from Stock and, as on iOS,
 * the one card page whichever list it was opened from.
 *
 * One change at a time: a save, a reprice or an archive holds the others off
 * until the card has been read again after it.
 */
export class CardDetailController extends SubtreeController implements CardDetailActions {
  readonly state = new CardDetailState();
  readonly #detail: Loader<{ shopId: string; cardId: string; read: number }, CardDetail>;
  #shopId: string | null = null;
  /** Numbers the price history reads, so an older answer never lands over a newer one. */
  #historyAsked = 0;
  /** The section to bring into view once the first read lands. */
  #focus: CardSection | null;
  /** The page's read may predate a write another page left on the card, until a read after it lands. */
  #stale = false;
  /** Other pages' writes on the card the page waits for, to read it again once none is left. */
  #waits = 0;
  /** The Stock list's entries the page waits for or made itself, so each is taken once and its own never waited for. */
  readonly #seen = new WeakSet<Promise<void>>();
  #leavingOwn = false;
  /** The page's reads of the card, numbered as they are asked, the newest shown, and those still on their way. */
  #asked = 0;
  #shown = 0;
  readonly #out = new Set<number>();
  /** Another page's write landed once this many reads were asked: a read asked after it shows the card with it. */
  #landedAt: number | null = null;

  constructor(
    readonly deps: CardDetailDeps,
    readonly cardId: string,
    readonly routing: CardDetailRouting,
    context: { quoteNote?: string | null; section?: CardSection | null } = {},
  ) {
    super();
    this.state.quoteNote.value = context.quoteNote ?? null;
    this.#focus = context.section ?? null;
    this.subtree.put(CardDetailState, this.state);
    this.subtree.put(CardDetailActions, this);
    this.#detail = this.own(
      new Loader({
        status: this.state.status,
        fetch: ({ shopId, cardId }: { shopId: string; cardId: string; read: number }) =>
          loadCard({ ...deps, shopId, cardId }),
        loaded: (detail, { read }) => this.#show(detail, read),
      }),
    );

    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      void this.#load().then((read) => this.#focusOnce(read));
    }, [deps.storeRepository]);
    // A count or a lookup another page left on the card may land after this page read it: read the card again then,
    // and what the merchant has not touched follows it. A page left by Back or Forward leaves its writes only once
    // this one is built, so the list is followed, not read once.
    this.#behind(deps.stockListRepository.left.get(cardId));
    this.subscribe(() => this.#behind(deps.stockListRepository.left.get(cardId)), [deps.stockListRepository]);
    // Null role (not yet resolved) behaves like today — allowed — until it is known.
    this.sync(() => {
      const role = deps.storeRepository.selectedStore?.myRole ?? null;
      this.state.canEditInventory.value = role === null || can(role, "inventoryEdit");
    }, [deps.storeRepository]);
  }

  setFields = (patch: Partial<CardFields>) => {
    this.state.fields.update((fields) => (fields ? { ...fields, ...patch } : fields));
  };

  setSeparateFloor = (on: boolean) => {
    this.state.fields.update((fields) =>
      fields
        ? { ...fields, separateFloor: on, floor: on && !fields.floor.trim() ? fields.price : fields.floor }
        : fields,
    );
  };

  setLossKind = (kind: StockLossKind) => {
    this.state.lossKind.value = kind;
  };

  setLossNote = (note: string) => {
    this.state.lossNote.value = note;
  };

  discard = () => {
    const detail = this.state.detail.value;
    if (!detail) return;
    this.state.fields.value = cardBase(detail);
    this.state.savingFrom.value = null;
    this.state.lossNote.value = "";
    this.state.error.value = null;
  };

  /**
   * What the card is first, since a new identity reprices the card; then a typed
   * price, which wins over that lookup; then the count. Each stops the rest when
   * refused, and the card is read again once all of them went through. One that
   * threw may have gone through, so the card is read again then too, and what it
   * shows is what was saved unless that read fails as well. The writes go on once
   * the page is left, as on iOS: only what the page says stops with it.
   */
  save = async () => {
    const shopId = this.#shopId;
    const typed = this.#editing();
    if (shopId === null || typed === null || !this.state.canEditInventory.value || this.#busy()) return;
    if (!isSaveable(typed)) return;
    const target = { ...this.deps, shopId, cardId: this.cardId };

    // After a Save whose card was not read again, a count is judged from the count that Save sent, until a read shows
    // the card: a Save that reads nothing (refused, or thrown with its read lost) keeps it.
    const kept = this.state.savingFrom.value;
    const from = kept ?? typed.base;
    const shownAt = this.#shown;
    let unreadFrom: CardFields | null = null;
    this.state.saving.value = true;
    this.state.savingFrom.value = from;
    this.state.error.value = null;
    this.state.archiveError.value = null;
    this.state.notice.value = null;
    // The Stock page Back lands on waits for these writes, or it reads the card as it was before them; and they wait
    // for any write another page left on the card, so the server takes them in the order they were made.
    const earlier = this.deps.stockListRepository.left.get(this.cardId);
    let written = () => {};
    this.#leaving(
      new Promise<void>((resolve) => {
        written = resolve;
      }),
    );
    try {
      await earlier;
      const editing = earlier !== undefined || this.#stale ? await this.#onto(typed, target) : typed;
      // The card as it turned out can leave an edit the rules refuse, or turn a Save pressed as one that keeps the card
      // into one that makes it another and reprices it: none of it is sent.
      const turned = changesIdentity(cardIdentityEdit(editing.fields, editing.card, editing.base), editing.card);
      const pressed = changesIdentity(cardIdentityEdit(typed.fields, typed.card, typed.base), typed.card);
      if ((isDirty(editing) && !isSaveable(editing)) || (turned && !pressed)) {
        if (!this.isDisposed) this.state.error.value = CARD_CHANGED_BEFORE_SAVE;
        return;
      }
      const { card, base, fields } = editing;
      const edit = cardIdentityEdit(fields, card, base);
      const priceCents = inputToCents(fields.price) ?? null;
      const stock = stockUpdate(editing, from);
      let lookupFailed: string | null = null;
      // The lookup's figure, when it found one: a price the merchant left alone gives way to it.
      let looked: number | null = null;
      if (!isEmptyEdit(edit)) {
        const edited = await editCard({ ...target, edit });
        if (!edited.ok) return this.#refused(edited.errors);
        const detail = this.state.detail.value;
        if (detail && !this.isDisposed) this.#apply({ ...detail, card: edited.value.card });
        if (edited.value.identityChanged) {
          const repriced = await repriceCard(target);
          if (!repriced.ok) lookupFailed = `Saved, but the price lookup failed: ${errorText(repriced.errors)}`;
          else if (priceCents === card.currentPriceCents) looked = repriced.value;
        }
      }

      if (pricesChanged(fields, card)) {
        // After a lookup the asking price is already its figure, and a floor
        // this save sets to follow the price follows that one. A save of what
        // the card is alone leaves the floor where it was, as iOS does. Only
        // what moved is sent: a price or a floor left alone stays as it is.
        if (looked === null && priceCents !== card.currentPriceCents) {
          const priced = await setCardPrice({ ...target, priceCents });
          if (!priced.ok) return this.#refused(priced.errors);
        }
        const floor = looked !== null && !fields.separateFloor ? looked : floorCentsOf(fields);
        if (floor !== card.floorPriceCents) {
          const floored = await setCardFloor({ ...target, floorPriceCents: floor });
          if (!floored.ok) return this.#refused(floored.errors);
        }
      }

      if (stock) {
        const counted = await setCardStock({ ...target, change: stock });
        if (!counted.ok) return this.#refused(counted.errors);
      }
      written();
      if (this.isDisposed) return;

      // Read back as the page shows it. A read that fails, refused or thrown,
      // leaves what was typed in place: every write here sets a value rather
      // than adding to one, so saving it again repeats nothing.
      const read = await this.#load().catch(() => false);
      if (this.isDisposed) return;
      if (!read) {
        this.state.error.value = lookupFailed ?? CARD_SAVED_UNREAD;
        if (stock) unreadFrom = { ...editing.base, onHand: fields.onHand };
        return;
      }
      const detail = this.state.detail.value;
      if (detail) this.state.fields.value = cardBase(detail);
      this.state.lossNote.value = "";
      this.state.error.value = lookupFailed;
      if (lookupFailed === null) this.state.notice.value = "Saved. The listing updates within a minute.";
    } catch (thrown) {
      written();
      await this.#threw(CARD_SAVE_FAILED_ON_THE_WAY, CARD_SAVE_FAILED_UNREAD);
      throw thrown;
    } finally {
      written();
      if (!this.isDisposed) {
        this.state.saving.value = false;
        this.state.savingFrom.value = unreadFrom ?? (this.#shown > shownAt ? null : kept);
      }
    }
  };

  /** Asks the market again. "Nothing found" is an answer, not a failure. */
  refreshPrice = async () => {
    const shopId = this.#shopId;
    if (shopId === null || !this.state.canEditInventory.value || this.#busy()) return;
    this.state.repricing.value = true;
    this.state.error.value = null;
    this.state.archiveError.value = null;
    this.state.notice.value = null;
    try {
      const r = await this.#writing(() => repriceCard({ ...this.deps, shopId, cardId: this.cardId })).catch(
        async (thrown: unknown) => {
          await this.#threw(REPRICE_FAILED_ON_THE_WAY);
          throw thrown;
        },
      );
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.state.notice.value =
        r.value == null ? "No eBay comps found. The price was left alone." : `Repriced to ${formatCents(r.value)}.`;
      // The reprice went through: a read after it that throws is said where the card's own read is.
      await this.#load().catch(() => false);
    } finally {
      if (!this.isDisposed) this.state.repricing.value = false;
    }
  };

  askArchive = () => {
    // Its question would wait on the write still on its way, with nothing to do but wait.
    if (!this.state.canEditInventory.value || this.#busy() || this.state.waiting.value) return;
    this.state.archiveError.value = null;
    this.state.confirmingArchive.value = true;
  };

  cancelArchive = () => {
    if (!this.state.archiving.value) this.state.confirmingArchive.value = false;
  };

  archive = async () => {
    const shopId = this.#shopId;
    if (shopId === null || !this.state.confirmingArchive.value || this.#busy()) return;
    this.state.archiving.value = true;
    this.state.error.value = null;
    try {
      const r = await this.#writing(() => archiveCard({ ...this.deps, shopId, cardId: this.cardId }));
      if (this.isDisposed) return;
      this.state.confirmingArchive.value = false;
      if (!r.ok) {
        this.state.archiveError.value = errorText(r.errors);
        return;
      }
      this.routing.toCards();
    } catch (thrown) {
      // The question stays until the read is in, so it closes as Archive card comes back and the focus can go there.
      await this.#threw(ARCHIVE_FAILED_ON_THE_WAY, ARCHIVE_FAILED_ON_THE_WAY, this.state.archiveError);
      if (!this.isDisposed) this.state.confirmingArchive.value = false;
      throw thrown;
    } finally {
      if (!this.isDisposed) this.state.archiving.value = false;
    }
  };

  retry = () => {
    if (!this.#busy()) void this.#load().then((read) => this.#focusOnce(read));
  };

  back = () => this.routing.back();

  scrolled = () => {
    this.state.scrollTo.value = null;
  };

  /** A write refused stops the rest of a save, and is said while the page is there. */
  #refused(errors: readonly Error[]): void {
    if (!this.isDisposed) this.state.error.value = errorText(errors);
  }

  /**
   * What a save sends, onto the card as it is now: a write another page left on the card may have landed since the
   * page read it, so the card is read again and what the merchant left alone follows it. A read that fails leaves the
   * save on what the page shows, its own read and any read since, which still sends only what moved there.
   */
  async #onto(typed: CardEditing, target: { shopId: string; cardId: string }): Promise<CardEditing> {
    const asked = this.#ask();
    const read = await loadCard({ ...this.deps, ...target }).catch(() => null);
    // The fields are held while saving: what the page shows is the click's edit on the newest read the page took.
    const shown = this.#editing() ?? typed;
    if (read?.ok && !this.isDisposed) this.#show(read.value, asked);
    this.#settled(asked);
    if (!read?.ok) return shown;
    const base = cardBase(read.value);
    return { ...shown, card: read.value.card, base, fields: rebasedFields(shown.fields, shown.base, base, true) };
  }

  /**
   * A write in the Stock list's ledger, as a save's: sent after any write another page left on the card, and waited
   * for by the Stock page Back lands on.
   */
  #writing<T>(call: () => Promise<T>): Promise<T> {
    const write = (this.deps.stockListRepository.left.get(this.cardId) ?? Promise.resolve()).then(call);
    this.#leaving(
      write.then(
        () => undefined,
        () => undefined,
      ),
    );
    return write;
  }

  /** Puts one of the page's own writes in the Stock list's ledger, as one the page does not wait for itself. */
  #leaving(landed: Promise<void>): void {
    const ledger = this.deps.stockListRepository;
    this.#leavingOwn = true;
    try {
      ledger.leaving(this.cardId, landed);
    } finally {
      this.#leavingOwn = false;
    }
    const entry = ledger.left.get(this.cardId);
    if (entry) this.#seen.add(entry);
  }

  /** Waits for a write another page left on the card, then reads the card again once none is left. */
  #behind(write: Promise<void> | undefined): void {
    if (write === undefined || this.#leavingOwn || this.#seen.has(write)) return;
    this.#seen.add(write);
    this.#stale = true;
    this.#waits++;
    this.state.waiting.value = true;
    void write.then(async () => {
      if (this.isDisposed || --this.#waits > 0) return;
      // The wait ends with a read asked after the landing, this one or a newer one: until one answers, the page shows
      // the card as it was.
      this.#landedAt = this.#asked;
      const read = await this.#load().catch(() => false);
      if (this.isDisposed) return;
      // A read that landed under this one was the first: the section a Stock row asked for comes now.
      this.#focusOnce(read);
    });
  }

  /**
   * Ends the wait for a landing once a read asked after it shows the card, or once none such is on its way: then the
   * page says it still shows the card as it was, until one does.
   */
  #caughtUp(): void {
    const at = this.#landedAt;
    if (at === null || this.#waits > 0 || this.isDisposed) return;
    const read = this.#shown > at;
    if (!read && [...this.#out].some((n) => n > at)) return;
    if (read) {
      this.#landedAt = null;
      this.#stale = false;
    }
    this.state.landedUnread.value = !read;
    this.state.waiting.value = false;
  }

  #busy(): boolean {
    return this.state.saving.value || this.state.repricing.value || this.state.archiving.value;
  }

  /** A call that threw: the card read again, since it may have gone through, then said as the read left it. */
  async #threw(sentence: string, unread = sentence, said = this.state.error): Promise<void> {
    if (this.isDisposed) return;
    const read = await this.#load().catch(() => false);
    if (!this.isDisposed) said.value = read ? sentence : unread;
  }

  #editing(): CardEditing | null {
    const detail = this.state.detail.value;
    const fields = this.state.fields.value;
    if (!detail || !fields) return null;
    return {
      card: detail.card,
      base: cardBase(detail),
      fields,
      lossKind: this.state.lossKind.value,
      lossNote: this.state.lossNote.value,
    };
  }

  #ask(): number {
    this.#out.add(++this.#asked);
    return this.#asked;
  }

  /** A read of the card, shown unless a newer one already is. */
  #show(detail: CardDetail, read: number): void {
    if (read < this.#shown) return;
    this.#shown = read;
    this.#apply(detail);
    // The card shows as read now: an earlier read's failure no longer stands over it.
    if (this.state.status.value.kind === "error") this.state.status.value = loaded;
    if (!this.state.saving.value) this.state.savingFrom.value = null;
  }

  /** A read answered, failed or was overtaken: an older read's failure does not stand over a newer card. */
  #settled(read: number): void {
    this.#out.delete(read);
    if (this.isDisposed) return;
    if (this.#shown > read && this.state.status.value.kind === "error") this.state.status.value = loaded;
    this.#caughtUp();
  }

  /** The card as read, with every field the merchant left alone following it. A Save holds the fields: no one types. */
  #apply(detail: CardDetail): void {
    const before = this.state.detail.value;
    const now = cardBase(detail);
    this.state.detail.value = detail;
    const held = this.state.saving.value;
    this.state.fields.update((fields) => (fields && before ? rebasedFields(fields, cardBase(before), now, held) : now));
  }

  /** Asks the page, once, for the section a Stock row's menu named, after the first read that answers. */
  #focusOnce(read: boolean): void {
    if (!read || this.isDisposed || this.#focus === null) return;
    this.state.scrollTo.value = this.#focus;
    this.#focus = null;
  }

  /** Reads the card and its prices; true when the card was read. */
  async #load(): Promise<boolean> {
    const shopId = this.#shopId;
    if (shopId === null) return false;
    const read = this.#ask();
    try {
      await this.#detail.load({ shopId, cardId: this.cardId, read });
    } finally {
      this.#settled(read);
    }
    if (this.isDisposed || this.state.status.value.kind !== "loaded") return false;
    const asked = ++this.#historyAsked;
    // The price history is a side list: a read of it that throws keeps the last one.
    const prices = await loadPriceHistory({ ...this.deps, shopId, cardId: this.cardId }).catch(() => null);
    if (this.isDisposed || asked !== this.#historyAsked) return true;
    if (prices?.ok) this.state.history.value = prices.value;
    return true;
  }
}
